import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Phone, ArrowRight, ArrowLeft, Info, Building2, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import {
  TERMS_CURRENT_VERSION, TERMS_VERSION_LABEL, PILOT_WARNING,
} from "@/config/legalText";

const previewPhone = (raw) => {
  const trimmed = (raw || "").trim();
  if (!trimmed) return "";
  const hadPlus = trimmed.startsWith("+");
  const digits = trimmed.replace(/\D/g, "");
  if (hadPlus) return "+" + digits;
  if (digits.length === 10) return "+52" + digits;
  if (digits.length === 12 && digits.startsWith("52")) return "+" + digits;
  if (digits.length === 11 && digits.startsWith("1")) return "+52" + digits.slice(1);
  return "+" + digits;
};

/**
 * LoginPage — Flujo de 4 pasos para Asesor Independiente y Subusuario:
 *   phone  → otp  → (profile if new)  → (terms if not accepted)  → /app
 *
 * Reglas del spec Fase 1:
 * - Solo teléfono (nunca email/Google) para registro nuevo.
 * - Nombre completo obligatorio antes de continuar, sólo si el usuario es nuevo.
 * - Aceptación de Términos + Aviso obligatoria antes de entrar.
 * - Botón Google visible pero sólo funciona para cuentas ya vinculadas (Fase B).
 * - Banner "cuenta de prueba" oculto si backend reporta `dev_otp_mode: false`.
 */
export default function LoginPage() {
  const [step, setStep] = useState("phone"); // phone | otp | profile | terms
  const [phone, setPhone] = useState("");
  const [normalized, setNormalized] = useState("");
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState("");
  const [devMode, setDevMode] = useState(null); // true | false | null(loading)
  const [nombrePila, setNombrePila] = useState("");
  const [apellidoPaterno, setApellidoPaterno] = useState("");
  const [apellidoMaterno, setApellidoMaterno] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, refresh } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    // Solo mostramos el banner "cuenta de prueba" si el backend está en modo dev.
    api.get("/").then(({ data }) => setDevMode(!!data?.dev_otp_mode)).catch(() => setDevMode(false));
  }, []);

  const phonePreview = previewPhone(phone);
  const phoneValid = /^\+\d{10,15}$/.test(phonePreview);

  // En mobile abrimos inline; en desktop nueva pestaña (spec C).
  const openLegal = (path) => (e) => {
    e.preventDefault();
    if (window.matchMedia("(max-width: 767px)").matches) {
      window.location.href = path;
    } else {
      window.open(path, "_blank", "noopener,noreferrer");
    }
  };

  const sendOtp = async (e) => {
    e?.preventDefault?.();
    if (!phoneValid) return toast.error("Verifica tu número de celular");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/send-otp", { phone });
      setNormalized(data.phone);
      setDevCode(data.dev_code || "");
      setStep("otp");
      if (data.dev_code) {
        toast.success(`Código enviado (dev): ${data.dev_code}`, { duration: 6000 });
      } else {
        toast.success("Código enviado por SMS");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error enviando código");
    } finally {
      setLoading(false);
    }
  };

  // Después de verificar OTP, avanzamos al paso que corresponda según el usuario.
  const routeAfterVerify = (user) => {
    if (!user?.nombre_pila) { setStep("profile"); return; }
    if (user.terms_version_accepted !== TERMS_CURRENT_VERSION) { setStep("terms"); return; }
    nav("/app");
  };

  const verify = async (fullCode) => {
    const use = fullCode || code;
    if (use.length !== 6) return;
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { phone: normalized, code: use });
      login(data.token, data.user);
      routeAfterVerify(data.user);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Código incorrecto");
      setCode("");
    } finally {
      setLoading(false);
    }
  };

  const submitProfile = async (e) => {
    e?.preventDefault?.();
    if (!nombrePila.trim() || !apellidoPaterno.trim()) {
      return toast.error("Nombre y apellido paterno son obligatorios");
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/complete-profile", {
        nombre_pila: nombrePila.trim(),
        apellido_paterno: apellidoPaterno.trim(),
        apellido_materno: apellidoMaterno.trim() || null,
      });
      await refresh();
      if (data.user?.terms_version_accepted !== TERMS_CURRENT_VERSION) {
        setStep("terms");
      } else {
        nav("/app");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo guardar tu nombre");
    } finally {
      setLoading(false);
    }
  };

  const submitTerms = async () => {
    if (!acceptTerms) return;
    setLoading(true);
    try {
      await api.post("/auth/accept-terms", { version: TERMS_CURRENT_VERSION });
      await refresh();
      toast.success("¡Bienvenido a Co-Legal!");
      nav("/app");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo registrar la aceptación");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // Fase B: activaremos el bridge a Emergent Auth. Por ahora avisamos.
    toast.info("Vincula tu Google desde tu perfil primero. Disponible en Fase B.");
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="max-w-6xl mx-auto w-full px-4 sm:px-6 pt-6">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900" data-testid="back-home-link">
          <ArrowLeft className="w-4 h-4" /> Volver al inicio
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2.5 mb-8 justify-center">
            <div className="leading-none text-center">
              <div className="font-heading font-extrabold text-slate-900 text-xl tracking-tight">Co-Legal</div>
            </div>
          </div>

          <div className="bm-card p-6 sm:p-8">
            {step === "phone" && (
              <form onSubmit={sendOtp} className="space-y-5" data-testid="login-phone-form">
                <div>
                  <h1 className="text-2xl font-heading font-bold text-slate-900">Entra con tu teléfono</h1>
                  <p className="text-sm text-slate-600 mt-1">Te enviaremos un código de 6 dígitos por SMS.</p>
                </div>

                <div>
                  <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Número de celular</label>
                  <div className="relative">
                    <Phone className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="login-phone-input"
                           className="w-full h-14 pl-11 pr-4 rounded-xl border border-[#CBD5E1] bg-white text-slate-900 focus:ring-2 focus:ring-[#1E3A5F] focus:border-[#1E3A5F] outline-none text-base"
                           placeholder="55 1234 5678" inputMode="tel" />
                  </div>
                  {phone.trim() ? (
                    <p className={`text-xs mt-1.5 ${phoneValid ? "text-[#2A5C55]" : "text-[#B54708]"}`} data-testid="phone-preview">
                      {phoneValid
                        ? <>Se enviará el SMS a <b>{phonePreview}</b></>
                        : "Formato inválido. Escribe 10 dígitos (México) o incluye lada internacional con +."}
                    </p>
                  ) : (
                    <p className="text-xs text-slate-500 mt-1.5">Formato México: 10 dígitos. Se antepone +52 automáticamente.</p>
                  )}
                </div>

                <button type="submit" disabled={loading || !phoneValid} className="bm-btn-primary w-full" data-testid="login-send-otp-btn">
                  {loading ? "Enviando…" : "Enviar código"} <ArrowRight className="w-4 h-4" />
                </button>

                <div className="relative flex items-center gap-3 py-1">
                  <div className="flex-1 h-px bg-slate-200" />
                  <span className="text-xs text-slate-500 uppercase tracking-wider">o continúa con</span>
                  <div className="flex-1 h-px bg-slate-200" />
                </div>

                <button type="button" onClick={googleLogin} data-testid="login-google-btn"
                        className="w-full h-12 rounded-xl border border-[#E2E8F0] bg-white text-slate-700 font-semibold hover:bg-slate-50 flex items-center justify-center gap-2.5 transition-colors">
                  <svg className="w-5 h-5" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                  </svg>
                  Entra con Google
                </button>
                <p className="text-[11px] text-slate-500 -mt-2 text-center">
                  Solo cuentas ya vinculadas. ¿Nuevo? Regístrate por teléfono.
                </p>

                <div className="pt-4 border-t border-slate-100">
                  <Link to="/inmobiliaria/login" className="text-xs text-slate-600 hover:text-[#1E3A5F] inline-flex items-center gap-1.5"
                        data-testid="login-agency-link">
                    <Building2 className="w-3.5 h-3.5" />
                    ¿Eres inmobiliaria con acceso por correo? Entra aquí
                  </Link>
                </div>

                {devMode && (
                  <div className="text-xs text-slate-500 flex items-start gap-2 bg-[#FEF9EC] border border-[#F5D57B] rounded-xl p-3"
                       data-testid="dev-test-account-hint">
                    <Info className="w-4 h-4 text-[#D99B26] shrink-0 mt-0.5" />
                    <span>Modo desarrollo activo. El código aparecerá en pantalla tras enviar.</span>
                  </div>
                )}
              </form>
            )}

            {step === "otp" && (
              <div className="space-y-6" data-testid="login-otp-form">
                <div>
                  <h1 className="text-2xl font-heading font-bold text-slate-900">Ingresa el código</h1>
                  <p className="text-sm text-slate-600 mt-1">
                    Enviamos un código a <b>{normalized}</b>
                  </p>
                  {devCode && (
                    <div className="mt-3 text-xs bg-[#FEF9EC] border border-[#F5D57B] rounded-xl p-3 text-[#92400E]" data-testid="dev-code-hint">
                      <b>Modo desarrollo:</b> tu código es <span className="font-mono text-base">{devCode}</span>
                    </div>
                  )}
                </div>

                <div className="flex justify-center">
                  <InputOTP maxLength={6} value={code} onChange={(v) => { setCode(v); if (v.length === 6) verify(v); }} data-testid="login-otp-input">
                    <InputOTPGroup>
                      <InputOTPSlot index={0} data-testid="otp-slot-0" />
                      <InputOTPSlot index={1} data-testid="otp-slot-1" />
                      <InputOTPSlot index={2} data-testid="otp-slot-2" />
                      <InputOTPSlot index={3} data-testid="otp-slot-3" />
                      <InputOTPSlot index={4} data-testid="otp-slot-4" />
                      <InputOTPSlot index={5} data-testid="otp-slot-5" />
                    </InputOTPGroup>
                  </InputOTP>
                </div>

                <button onClick={() => verify()} disabled={loading || code.length !== 6} className="bm-btn-primary w-full" data-testid="login-verify-btn">
                  {loading ? "Verificando…" : "Verificar y entrar"}
                </button>

                <div className="flex items-center justify-between text-sm">
                  <button type="button" onClick={() => setStep("phone")} className="text-slate-600 hover:text-slate-900" data-testid="login-change-phone-btn">
                    Cambiar número
                  </button>
                  <button type="button" onClick={sendOtp} className="text-[#1E3A5F] font-semibold" data-testid="login-resend-btn">
                    Reenviar código
                  </button>
                </div>
              </div>
            )}

            {step === "profile" && (
              <form onSubmit={submitProfile} className="space-y-5" data-testid="login-profile-form">
                <div>
                  <h1 className="text-2xl font-heading font-bold text-slate-900">¡Falta un paso más!</h1>
                  <p className="text-sm text-slate-600 mt-1">Cuéntanos tu nombre completo para personalizar tu cuenta.</p>
                </div>

                <div>
                  <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Nombre(s) *</label>
                  <input value={nombrePila} onChange={(e) => setNombrePila(e.target.value)} data-testid="login-nombre-pila-input"
                         className="w-full h-12 px-4 rounded-xl border border-[#CBD5E1] bg-white text-slate-900 focus:ring-2 focus:ring-[#1E3A5F] focus:border-[#1E3A5F] outline-none"
                         placeholder="Ej. María" required />
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Apellido paterno *</label>
                  <input value={apellidoPaterno} onChange={(e) => setApellidoPaterno(e.target.value)} data-testid="login-apellido-paterno-input"
                         className="w-full h-12 px-4 rounded-xl border border-[#CBD5E1] bg-white text-slate-900 focus:ring-2 focus:ring-[#1E3A5F] focus:border-[#1E3A5F] outline-none"
                         placeholder="Ej. López" required />
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Apellido materno <span className="text-slate-400 font-normal">(opcional)</span></label>
                  <input value={apellidoMaterno} onChange={(e) => setApellidoMaterno(e.target.value)} data-testid="login-apellido-materno-input"
                         className="w-full h-12 px-4 rounded-xl border border-[#CBD5E1] bg-white text-slate-900 focus:ring-2 focus:ring-[#1E3A5F] focus:border-[#1E3A5F] outline-none"
                         placeholder="Ej. Hernández" />
                </div>

                <button type="submit" disabled={loading || !nombrePila.trim() || !apellidoPaterno.trim()}
                        className="bm-btn-primary w-full" data-testid="login-profile-submit-btn">
                  {loading ? "Guardando…" : "Continuar"} <ArrowRight className="w-4 h-4" />
                </button>
              </form>
            )}

            {step === "terms" && (
              <div className="space-y-5" data-testid="login-terms-form">
                <div>
                  <h1 className="text-2xl font-heading font-bold text-slate-900">Un último paso legal</h1>
                  <p className="text-sm text-slate-600 mt-1">
                    Para usar Co-Legal necesitamos tu consentimiento explícito.
                  </p>
                </div>

                <div className="rounded-xl border border-[#FCD34D] bg-[#FEF3C7] text-[#92400E] p-3 text-xs" data-testid="login-terms-pilot-warning">
                  <b>Versión piloto {TERMS_VERSION_LABEL}:</b> {PILOT_WARNING}
                </div>

                <label className="flex items-start gap-3 cursor-pointer" data-testid="login-terms-checkbox-label">
                  <input
                    type="checkbox"
                    checked={acceptTerms}
                    onChange={(e) => setAcceptTerms(e.target.checked)}
                    data-testid="login-terms-checkbox"
                    className="mt-1 w-5 h-5 rounded border-[#CBD5E1] text-[#1E3A5F] focus:ring-[#1E3A5F]"
                  />
                  <span className="text-sm text-slate-700">
                    He leído y acepto el{" "}
                    <a href="/aviso-privacidad" onClick={openLegal("/aviso-privacidad")}
                       className="text-[#1E3A5F] font-semibold underline inline-flex items-center gap-1"
                       data-testid="login-terms-link-privacy">
                      Aviso de Privacidad <ExternalLink className="w-3 h-3" />
                    </a>{" "}y los{" "}
                    <a href="/terminos" onClick={openLegal("/terminos")}
                       className="text-[#1E3A5F] font-semibold underline inline-flex items-center gap-1"
                       data-testid="login-terms-link-terms">
                      Términos de Uso <ExternalLink className="w-3 h-3" />
                    </a>{" "}({TERMS_VERSION_LABEL}).
                  </span>
                </label>

                <button type="button" onClick={submitTerms} disabled={loading || !acceptTerms}
                        className="bm-btn-primary w-full" data-testid="login-terms-submit-btn">
                  {loading ? "Guardando…" : "Entrar a Co-Legal"} <ArrowRight className="w-4 h-4" />
                </button>

                <p className="text-[11px] text-slate-400 text-center">
                  Registramos fecha, hora y versión aceptada en tu perfil.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
